import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Spinner, makeStyles, tokens } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";
import { useAtomValue, useSetAtom } from "jotai";
import { isRTLAtom, cultureAtom, isAuthenticatedAtom } from "./store";
import { authLoadingAtom } from "./store/atoms/authAtoms";
import { ThemeProviderWrapper } from "./styles/ThemeProviderWrapper";
import { AuthProvider } from "./auth/AuthProvider";
import { AuthenticatedRegistrationRoute } from "./components/AuthenticatedRegistrationRoute";

// Pages - Lazy loaded
const Login = React.lazy(() => import("./pages/auth/Login"));
const AuthCallback = React.lazy(() => import("./auth/AuthCallback"));
const Register = React.lazy(() => import("./pages/registration/Register"));
const PendingApproval = React.lazy(() => import("./pages/registration/PendingApproval"));
const AcceptHandoff = React.lazy(() => import("./pages/auth/AcceptHandoff"));
const Dashboard = React.lazy(() => import("./pages/gir/Dashboard"));
const SubmissionList = React.lazy(() => import("./pages/gir/SubmissionList"));
const SubmissionWizard = React.lazy(() => import("./pages/gir/SubmissionWizard"));
const SubmissionView = React.lazy(() => import("./pages/gir/SubmissionView"));
const XmlUpload = React.lazy(() => import("./pages/gir/XmlUpload"));
const Profile = React.lazy(() => import("./pages/Profile"));
const NonFilingNotification = React.lazy(
  () => import("./pages/notifications/NonFilingNotification"),
);
const NonFilingNotificationList = React.lazy(
  () => import("./pages/notifications/NonFilingNotificationList"),
);
const DLEManagement = React.lazy(() => import("./pages/dle/DLEManagement"));
const AgentManagement = React.lazy(() => import("./pages/agents/AgentManagement"));
const MessagesPage = React.lazy(() => import("./pages/messages/MessagesPage"));
const ReceivedGirList = React.lazy(() => import("./pages/received/ReceivedGirList"));
const ReceivedGirDetail = React.lazy(() => import("./pages/received/ReceivedGirDetail"));
const MneGroupHome = React.lazy(() => import("./pages/group/MneGroupHome"));

// Layout
const Layout = React.lazy(() => import("./layouts/Layout"));

// Layout styles
const useLayoutStyles = makeStyles({
  app: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
    width: "100%",
    overflowX: "hidden",
    overflowY: "auto",
    backgroundColor: tokens.colorNeutralBackground3,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100vh",
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

// Loading fallback
const LoadingFallback = () => {
  const layoutStyles = useLayoutStyles();
  // useSuspense: false prevents an infinite loop when this component is itself a Suspense fallback
  const { t } = useTranslation("pillar2portal-common", { useSuspense: false });
  return (
    <div className={layoutStyles.loadingContainer}>
      <Spinner size="extra-large" label={t("loading", "Loading...")} />
    </div>
  );
};

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const isAuthLoading = useAtomValue(authLoadingAtom);

  if (isAuthLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  const isRTL = useAtomValue(isRTLAtom);
  const setCulture = useSetAtom(cultureAtom);
  const { i18n } = useTranslation();
  const layoutStyles = useLayoutStyles();

  // Sync i18next language with cultureAtom on mount and when language changes
  React.useEffect(() => {
    // Initialize cultureAtom from i18next on mount
    if (i18n.language) {
      setCulture(i18n.language);
    }

    // Listen to i18next language changes
    const handleLanguageChange = (lng: string) => {
      setCulture(lng);
    };

    i18n.on("languageChanged", handleLanguageChange);

    return () => {
      i18n.off("languageChanged", handleLanguageChange);
    };
  }, [i18n, setCulture]);

  // Update document direction when RTL changes
  React.useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  return (
    <Suspense fallback={<LoadingFallback />}>
      <ThemeProviderWrapper>
        <AuthProvider>
          <BrowserRouter>
            <div className={layoutStyles.app}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <Login />
                    </PublicRoute>
                  }
                />
                <Route path="/auth/callback" element={<AuthCallback />} />
                {/*
                  REQ-PORTAL-0001-4e184: /register uses AuthenticatedRegistrationRoute so
                  Keycloak-authenticated but portal-unlinked users can reach the wizard. /login
                  stays on PublicRoute — only /register swaps.
                */}
                <Route
                  path="/register"
                  element={
                    <AuthenticatedRegistrationRoute>
                      <Register />
                    </AuthenticatedRegistrationRoute>
                  }
                />
                <Route path="/pending" element={<PendingApproval />} />
                <Route path="/auth/accept-handoff" element={<AcceptHandoff />} />

                {/* Protected routes with layout */}
                <Route
                  element={
                    <ProtectedRoute>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/submissions" element={<SubmissionList />} />
                  <Route path="/submissions/new" element={<SubmissionWizard />} />
                  <Route path="/submissions/upload-xml" element={<XmlUpload />} />
                  <Route path="/submissions/edit/:draftId" element={<SubmissionWizard />} />
                  <Route path="/submissions/view/:submissionId" element={<SubmissionView />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/notifications" element={<NonFilingNotificationList />} />
                  <Route path="/notifications/new" element={<NonFilingNotification />} />
                  <Route path="/dle-management" element={<DLEManagement />} />
                  <Route path="/agents" element={<AgentManagement />} />
                  <Route path="/messages" element={<MessagesPage />} />
                  <Route path="/received" element={<ReceivedGirList />} />
                  <Route path="/received/:id" element={<ReceivedGirDetail />} />
                  <Route path="/group" element={<MneGroupHome />} />
                </Route>

                {/* Catch all - redirect to landing or dashboard */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProviderWrapper>
    </Suspense>
  );
}

export default App;
